import { format as formatTZ, toZonedTime } from "date-fns-tz";
import { useMemo } from "react";
import { useStore } from "zustand";
import {
  getIdLabel,
  getSelectedDynamicFiltersWithLabels,
  getSelectedStaticFiltersWithLabels,
} from "@/components/news-feed/utils/filter-lookups";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { derive } from "@/lib/derive-zustand";
import { calculateDateRangeFromDays } from "@/lib/utils/dateUtils";
import {
  buildAndFromSelected,
  type SelectedFiltersInput,
} from "@/lib/utils/filters";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import { useAnalysisParamsContext } from "./analysis-params.context";
import useNewsFeedParamsStore, {
  DEFAULT_FILTER_DAYS,
} from "./newsFeed-params.slice";

/**
 * Derives the effective start and end dates based on current feed params.
 * Returns dates formatted as "yyyy-MM-dd" in UTC
 */
const deriveNewsFeedDates = derive((get) => {
  const { filterDays, isCustomDaysRange, startDate, endDate } = get(
    useNewsFeedParamsStore,
  );
  const timeZone = "UTC";

  let dateStart: Date;
  let dateEnd: Date;

  if (isCustomDaysRange && startDate && endDate) {
    dateStart = startDate;
    dateEnd = endDate;
  } else {
    // Use filterDays if available, otherwise fall back to default
    // This handles edge cases where filterDays might be null
    const daysToUse = filterDays ?? DEFAULT_FILTER_DAYS;
    const { startDate: calcStart, endDate: calcEnd } =
      calculateDateRangeFromDays(daysToUse);
    dateStart = calcStart;
    dateEnd = calcEnd;
  }

  return {
    startDate: formatTZ(toZonedTime(dateStart, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    endDate: formatTZ(toZonedTime(dateEnd, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
  };
});

/**
 * Derives the filter object shape for API queries (AND/OR) from selected params.
 * Keys are lowercased to match server-side field names.
 * Handles key message AND/OR logic based on selectedKeyMessageFilterExclusion.
 */
const deriveNewsFeedSelectedFilters = derive((get) => {
  const { selectedFilters } = get(useNewsFeedParamsStore);
  const { selectedKeyMessageFilterExclusion } = get(useKeyMessageStore);

  // Determine which categories should use AND logic
  const andCategories =
    selectedKeyMessageFilterExclusion === "AND"
      ? new Set(["KEY_MESSAGE"])
      : undefined;

  return buildAndFromSelected(selectedFilters, andCategories);
});

/**
 * Derives selected static filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 */
const deriveSelectedStaticFiltersWithLabels = derive((get) => {
  const { selectedFilters } = get(useNewsFeedParamsStore);
  // Access the stores reactively through get() to ensure proper dependency tracking
  const filterDictionaryStore = get(useFilterDictionary);
  return getSelectedStaticFiltersWithLabels(
    selectedFilters,
    filterDictionaryStore,
  );
});

/**
 * Derives selected dynamic filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 */
const deriveSelectedDynamicFiltersWithLabels = derive((get) => {
  const { selectedFilters } = get(useNewsFeedParamsStore);
  const newsFeedFiltersStore = get(useNewsFeedFilters);

  // Access dynamicFilters directly through the proxy to ensure proper dependency tracking
  // This ensures the derive system tracks changes to the dynamicFilters Map
  const dynamicFilters = newsFeedFiltersStore.dynamicFilters;

  // Convert selectedFilters to array and filter only dynamic categories
  const dynamicSelectedFilters = new Map<string, string[]>();
  for (const [category, selectedIds] of selectedFilters) {
    if (dynamicFilters.has(category)) {
      dynamicSelectedFilters.set(category, selectedIds);
    }
  }

  // Pass the store state to the utility function
  return getSelectedDynamicFiltersWithLabels(
    dynamicSelectedFilters,
    newsFeedFiltersStore,
  );
});

export const useNewsFeedDates = () =>
  useStore(deriveNewsFeedDates) as {
    startDate: string;
    endDate: string;
  };

export const useNewsFeedSelectedFilters = () =>
  useStore(deriveNewsFeedSelectedFilters) as SelectedFiltersInput;

/**
 * Selector hook that gets selected static filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 */
export const useSelectedStaticFiltersWithLabels = () =>
  useStore(deriveSelectedStaticFiltersWithLabels) as Map<
    ShareVoiceSidebarCategory,
    string[]
  >;

/**
 * Selector hook that gets selected dynamic filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 */
export const useSelectedDynamicFiltersWithLabels = () =>
  useStore(deriveSelectedDynamicFiltersWithLabels) as Map<string, string[]>;

export type SelectedNarrativeCluster = {
  id: string;
  name: string;
};

/**
 * Derives the IDs and names of selected narrative clusters.
 * Returns an array of objects containing both cluster IDs and names (labels) for clusters that are currently selected.
 */
const deriveSelectedNarrativeClusters = derive<SelectedNarrativeCluster[]>(
  (get) => {
    const { selectedFilters } = get(useNewsFeedParamsStore);
    const newsFeedFiltersStore = get(useNewsFeedFilters);

    // Filter to only narrative cluster category
    const narrativeClusterIds = selectedFilters.get("NARRATIVE_CLUSTER");
    if (!narrativeClusterIds || narrativeClusterIds.length === 0) {
      return [];
    }

    // Look up each ID individually to get its label
    return narrativeClusterIds
      .map((id) => {
        const name = getIdLabel("NARRATIVE_CLUSTER", id, newsFeedFiltersStore);
        // Only include clusters with valid names (skip if name is empty)
        return name ? { id, name } : null;
      })
      .filter(
        (cluster): cluster is SelectedNarrativeCluster => cluster !== null,
      );
  },
);

/**
 * Hook to get the IDs and names of selected narrative clusters.
 * When inside {@link AnalysisParamsProvider}, uses analysis URL params (`selectedFilters`).
 * Otherwise uses {@link useNewsFeedParamsStore} via {@link deriveSelectedNarrativeClusters}.
 *
 * @example
 * ```typescript
 * const selectedClusters = useSelectedNarrativeClusters();
 *  Returns: [
 *    { id: '123', name: 'Technology' },
 *    { id: '456', name: 'Sports' },
 *    { id: '789', name: 'Politics' }
 *  ]
 * ```
 */
export const useSelectedNarrativeClusters = (): SelectedNarrativeCluster[] => {
  const analysis = useAnalysisParamsContext();
  const newsFeedFiltersState = useNewsFeedFilters((state) => state);
  const storeSelected = useStore(deriveSelectedNarrativeClusters);

  return useMemo(() => {
    if (analysis?.selectedFilters?.NARRATIVE_CLUSTER?.length) {
      const ids = analysis.selectedFilters.NARRATIVE_CLUSTER;
      return ids
        .map((id) => {
          const name = getIdLabel(
            "NARRATIVE_CLUSTER",
            id,
            newsFeedFiltersState,
          );
          return name ? { id, name } : null;
        })
        .filter((c): c is SelectedNarrativeCluster => c !== null);
    }
    return storeSelected;
  }, [
    analysis?.selectedFilters?.NARRATIVE_CLUSTER,
    storeSelected,
    newsFeedFiltersState,
  ]);
};
